import type { UpdatesConfig, Cache } from '@urql/exchange-graphcache';
import { CustomerAddressBookDocument } from '../generated/shopping/CustomerAddressBookDocument';
import type { CustomerAddressBookQuery } from '../generated/shopping/graphql';

type AddressBookResult = Extract<
  CustomerAddressBookQuery['customerAddressBook'],
  { __typename?: 'CustomerAddressBookResult' }
>;

type MutationResultWithAddressBook = {
  __typename?: string;
  addressBook?: AddressBookResult['addressBook'];
};

/**
 * Extracts the addressBook from a successful address book mutation response
 * and writes it into all cached CustomerAddressBook queries so the UI updates.
 */
const updateAddressBookCache = (result: MutationResultWithAddressBook, cache: Cache) => {
  if (result.__typename !== 'CustomerChangeCustomerResultWithAddressBookAndOptions') return;

  const { addressBook } = result;
  if (!addressBook) return;

  const cachedQueries = cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === 'customerAddressBook');

  for (const query of cachedQueries) {
    cache.updateQuery(
      {
        query: CustomerAddressBookDocument,
        variables: query.arguments as { locale: string },
      },
      () => ({
        customerAddressBook: {
          __typename: 'CustomerAddressBookResult' as const,
          addressBook,
        },
      }),
    );
  }
};

const withAddressBookUpdate =
  (fieldName: string) =>
  (result: Record<string, unknown>, _args: Record<string, unknown>, cache: Cache) => {
    const mutationResult = result[fieldName];
    if (mutationResult) {
      updateAddressBookCache(mutationResult, cache);
    }
  };

export const addressBookCacheUpdates: UpdatesConfig['Mutation'] = {
  deleteAddressBookAddress: withAddressBookUpdate('deleteAddressBookAddress'),
  addAddressBookAddress: withAddressBookUpdate('addAddressBookAddress'),
  updateAddressBookAddress: withAddressBookUpdate('updateAddressBookAddress'),
  useAsDefaultDeliveryAddress: withAddressBookUpdate('useAsDefaultDeliveryAddress'),
  updateAccountAddress: withAddressBookUpdate('updateAccountAddress'),
};
